import { useState, useEffect, ReactNode } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { getDynamicMsalInstance } from 'config/authConfig';
import Loader from 'components/Loader';
import { useThemeConfigContext } from 'contexts/ThemeConfigContext';

interface DynamicMsalProviderProps {
  children: ReactNode;
}

const DynamicMsalProvider = ({ children }: DynamicMsalProviderProps) => {
  const [msalInstanceToUse, setMsalInstanceToUse] = useState<PublicClientApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const { themeConfig } = useThemeConfigContext();

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        let clientId: string = '';
        let tenantId: string = '';

        console.log('Initializing MSAL with theme config...', themeConfig);
        
        if (themeConfig?.clientConfig?.entraId?.enabled && themeConfig?.microsoftAuth) {
          clientId = themeConfig.microsoftAuth.clientId || '';
          tenantId = themeConfig.microsoftAuth.tenantId || '';
          console.log('Dynamic MSAL config loaded from theme:', { clientId, tenantId });
        } else {
          console.log('No Microsoft auth config found in theme, using fallback values');
        }
        
        // Get or create the dynamic MSAL instance
        const dynamicInstance = getDynamicMsalInstance(clientId, tenantId);
        setMsalInstanceToUse(dynamicInstance);
        
      } catch (error) {
        console.warn('Failed to initialize MSAL with theme config, using fallback instance:', error);
        // Fallback to default instance if configuration fails
        const fallbackInstance = getDynamicMsalInstance();
        setMsalInstanceToUse(fallbackInstance);
      } finally {
        setLoading(false);
      }
    };

    initializeMsal();
  }, [themeConfig]);

  if (loading || !msalInstanceToUse) {
    return <Loader />;
  }

  return (
    <MsalProvider instance={msalInstanceToUse}>
      {children}
    </MsalProvider>
  );
};

export default DynamicMsalProvider;